import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { create as createStore } from 'zustand';

import { sdk } from '@/services';

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 6;

const PostService = sdk.getService('PostService');

interface IStore {
  limit: number;
  selectedTopic?: string;
  setLimit: (limit: number) => void;
  setSelectedTopic: (topic?: string) => void;
}

const store = createStore<IStore>(set => ({
  limit: DEFAULT_LIMIT,
  selectedTopic: undefined,
  setLimit: (limit: number) => set({ limit }),
  setSelectedTopic: (selectedTopic?: string) => {
    set({ selectedTopic, limit: DEFAULT_LIMIT });
  }
}));

export const usePostsList = () => {
  const { limit, selectedTopic, setLimit, setSelectedTopic } = store();
  const queryKey = [
    'posts',
    `page-${DEFAULT_PAGE}`,
    `limit-${limit}`,
    `topic-${selectedTopic || 'all'}`
  ];

  const { isPending, data } = useQuery({
    queryKey,
    queryFn: () => {
      return PostService.getPosts({ page: DEFAULT_PAGE, limit }, { topic: selectedTopic });
    },
    placeholderData: keepPreviousData
  });

  const loadMorePosts = () => {
    if (isPending || (data?.docs.length ?? 0) < limit) return;
    setLimit(limit + DEFAULT_LIMIT);
  };

  const pagination = data && {
    page: data.page,
    limit: data.limit,
    hasNextPage: data.hasNextPage,
    hasPrevPage: data.hasPrevPage,
    nextPage: data.nextPage,
    pagingCounter: data.pagingCounter,
    prevPage: data.prevPage,
    totalDocs: data.totalDocs,
    totalPages: data.totalPages
  };

  return {
    isPending,
    loadMorePosts,
    pagination,
    posts: data?.docs,
    selectedTopic,
    setSelectedTopic
  };
};
